import { CircularProgress } from '@material-ui/core';
import { ExportIcon } from 'assets/icon/dynamic';
import {
  ChartTitle,
  ChartWrapper,
  ReportWrapper,
  colors,
} from 'components/Report/styles';
import React from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Format a timeSeries bucket for the X axis.
// "YYYY-MM-DD" (day) -> as-is. "YYYY-MM-DD-HH" (hour) -> "HH:00".
const formatBucket = (bucket) => {
  if (!bucket) return '';
  const parts = bucket.split('-');
  if (parts.length === 4) return `${parts[3]}:00`;
  return bucket;
};

// Round to at most 2 decimals, keep as number.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const StatCard = ({ label, value }) => (
  <div className="analytic-stat-card">
    <span className="analytic-stat-value">{value}</span>
    <span className="analytic-stat-label">{label}</span>
  </div>
);

const GazeAnalyticReport = (props) => {
  const { analytics, loading, onExport } = props;

  const totals = analytics?.totals || {};
  const timeSeries = Array.isArray(analytics?.timeSeries)
    ? analytics.timeSeries
    : [];
  const byLocation = Array.isArray(analytics?.byLocation)
    ? analytics.byLocation
    : [];

  const hasData = timeSeries.length > 0 || byLocation.length > 0;

  const timeLabels = timeSeries.map((d) => formatBucket(d.bucket));

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: { y: { beginAtZero: true } },
    plugins: {
      legend: { position: 'top', align: 'end' },
      datalabels: { display: false },
    },
  };

  const barOptions = (horizontal = false) => ({
    indexAxis: horizontal ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    scales: { x: { beginAtZero: true }, y: { beginAtZero: true } },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
    },
  });

  const impressionsOverTime = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Impressions',
        data: timeSeries.map((d) => d.impressions),
        borderColor: '#2C82F6',
        backgroundColor: '#2C82F6',
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };

  const sessionsVsViewers = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Sessions',
        data: timeSeries.map((d) => d.sessions),
        borderColor: '#60d17e',
        backgroundColor: '#60d17e',
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: 'Unique Viewers',
        data: timeSeries.map((d) => d.uniqueViewers),
        borderColor: '#7c5cdb',
        backgroundColor: '#7c5cdb',
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };

  const avgGazeOverTime = {
    labels: timeLabels,
    datasets: [
      {
        label: 'Avg Gaze (s)',
        data: timeSeries.map((d) => round2(d.avgGaze)),
        borderColor: '#fa8c5c',
        backgroundColor: '#fa8c5c',
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  };

  const locationLabels = byLocation.map((d) => d.location);

  const impressionsByLocation = {
    labels: locationLabels,
    datasets: [
      {
        label: 'Impressions',
        data: byLocation.map((d) => d.impressions),
        backgroundColor: '#2C82F6',
        borderRadius: 5,
      },
    ],
  };

  const avgGazeByLocation = {
    labels: locationLabels,
    datasets: [
      {
        label: 'Avg Gaze (s)',
        data: byLocation.map((d) => round2(d.avgGaze)),
        backgroundColor: '#fa8c5c',
        borderRadius: 5,
      },
    ],
  };

  const locationShare = {
    labels: locationLabels,
    datasets: [
      {
        data: byLocation.map((d) => d.impressions),
        backgroundColor: colors,
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return (
      <div className="analytic-chart">
        <ReportWrapper>
          <CircularProgress />
        </ReportWrapper>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="analytic-chart">
        <ReportWrapper>
          <ChartTitle>Gaze Analytic</ChartTitle>
          <p>No gaze data for the selected range.</p>
        </ReportWrapper>
      </div>
    );
  }

  return (
    <div className="analytic-chart">
      <ReportWrapper>
        <div className="gaze-report-header">
          <ChartTitle>Gaze Analytic</ChartTitle>
          {onExport && (
            <button className="btn-outline-primary" onClick={onExport}>
              <ExportIcon color="#64D5D2" size={18} />
              Export
            </button>
          )}
        </div>

        {/* Summary KPI cards */}
        <div className="analytic-stat-row">
          <StatCard
            label="Impressions"
            value={(totals.impressions ?? 0).toLocaleString()}
          />
          <StatCard
            label="Sessions"
            value={(totals.sessions ?? 0).toLocaleString()}
          />
          <StatCard
            label="Unique Viewers"
            value={(totals.uniqueViewers ?? 0).toLocaleString()}
          />
          <StatCard label="Avg Gaze (s)" value={round2(totals.avgGaze)} />
        </div>
      </ReportWrapper>

      {/* Impressions over time */}
      <ReportWrapper>
        <ChartTitle>Impressions Over Time</ChartTitle>
        <ChartWrapper style={{ height: 300, width: '100%', margin: 0 }}>
          <Line data={impressionsOverTime} options={lineOptions} />
        </ChartWrapper>
      </ReportWrapper>

      {/* Sessions vs unique viewers */}
      <ReportWrapper>
        <ChartTitle>Sessions vs Unique Viewers</ChartTitle>
        <ChartWrapper style={{ height: 300, width: '100%', margin: 0 }}>
          <Line data={sessionsVsViewers} options={lineOptions} />
        </ChartWrapper>
      </ReportWrapper>

      {/* Avg gaze over time */}
      <ReportWrapper>
        <ChartTitle>Avg Gaze Duration Over Time</ChartTitle>
        <ChartWrapper style={{ height: 300, width: '100%', margin: 0 }}>
          <Line data={avgGazeOverTime} options={lineOptions} />
        </ChartWrapper>
      </ReportWrapper>

      {/* By-location breakdown */}
      <ReportWrapper>
        <ChartTitle>Impressions by Location</ChartTitle>
        <ChartWrapper style={{ height: 300, width: '100%', margin: 0 }}>
          <Bar data={impressionsByLocation} options={barOptions(true)} />
        </ChartWrapper>
      </ReportWrapper>

      <ReportWrapper>
        <ChartTitle>Avg Gaze by Location</ChartTitle>
        <ChartWrapper style={{ height: 300, width: '100%', margin: 0 }}>
          <Bar data={avgGazeByLocation} options={barOptions(false)} />
        </ChartWrapper>
      </ReportWrapper>

      {/* Location share — donut only when few enough slices to read */}
      {byLocation.length > 0 && byLocation.length <= 5 && (
        <ReportWrapper>
          <ChartTitle>Location Share of Impressions</ChartTitle>
          <ChartWrapper
            style={{ height: 300, width: '100%', maxWidth: 360, margin: '0 auto' }}
          >
            <Doughnut
              data={locationShare}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const value = ctx.parsed || 0;
                        const total = ctx.dataset.data.reduce(
                          (a, b) => a + (Number(b) || 0),
                          0
                        );
                        const pct = total
                          ? round2((value / total) * 100)
                          : 0;
                        return `${ctx.label}: ${value.toLocaleString()} (${pct}%)`;
                      },
                    },
                  },
                  datalabels: {
                    display: true,
                    color: '#fff',
                    font: { weight: 'bold' },
                    formatter: (value, ctx) => {
                      const total = ctx.dataset.data.reduce(
                        (a, b) => a + (Number(b) || 0),
                        0
                      );
                      const pct = total ? round2((value / total) * 100) : 0;
                      return `${pct}%`;
                    },
                  },
                },
              }}
            />
          </ChartWrapper>
        </ReportWrapper>
      )}
    </div>
  );
};

export default GazeAnalyticReport;
